/**
 * 聊天面板：公开发言、悄悄话、公告。
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { applyMention, filterNames, insertAt, MENTION_ALL, mentionQuery, mentionsMe } from '../lib/chatText'
import { hasPerm, Limits, Perm, USER_ALL } from '../lib/protocol'
import { useRoomStore } from '../stores/roomStore'
import { EmojiPicker, MentionPop, RichText } from './ChatExtras'
import { IcSmile } from './Icons'

export function Chat() {
  const messages = useRoomStore((s) => s.messages)
  const members = useRoomStore((s) => s.members)
  const selfId = useRoomStore((s) => s.selfId)
  const sendText = useRoomStore((s) => s.sendText)
  const pmPeer = useRoomStore((s) => s.pmPeer)

  const [draft, setDraft] = useState('')
  const [target, setTarget] = useState<number>(USER_ALL)
  // 上一次的私聊对象，用来判断私聊结束时该不该把目标收回「对所有人」。
  const prevPeerRef = useRef<number>(-1)
  const [broadcast, setBroadcast] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  const stickRef = useRef(true)
  const taRef = useRef<HTMLTextAreaElement>(null)
  const [caret, setCaret] = useState(0)
  const [emojiOpen, setEmojiOpen] = useState(false)
  const [mentionIdx, setMentionIdx] = useState(0)
  // 按 Esc 收起候选后，继续打字前不再弹出。
  const [mentionOff, setMentionOff] = useState(false)

  const me = members.get(selfId)
  const canWrite = !!me && hasPerm(me.prop, Perm.Write)
  const canBroadcast = !!me && hasPerm(me.prop, Perm.Broadcast)

  // 只在用户本来就贴着底部时才自动滚动，
  // 否则正在往回翻聊天记录的人会被不断拽回底部。
  useEffect(() => {
    const el = listRef.current
    if (el && stickRef.current) el.scrollTop = el.scrollHeight
  }, [messages])

  const onScroll = () => {
    const el = listRef.current
    if (!el) return
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
  }

  const targets = useMemo(
    () => [...members.values()].filter((m) => m.id !== selfId),
    [members, selfId],
  )

  // 私聊建立后，把发送目标切到对方；私聊结束再切回「对所有人」。
  //
  // 不切的话会出隐私事故：横幅明明写着「私聊中」，输入框的目标却还停在
  // 「对所有人」—— 两个人以为在说悄悄话，实际每句都发给了全班。
  // 界面上的状态和真正生效的目标必须是同一件事。
  useEffect(() => {
    // 先把旧值取到局部变量再更新 ref。
    //
    // setTarget 的回调是异步跑的，而给 ref 赋值是同步的 ——
    // 直接在回调里读 prevPeerRef.current，读到的已经是刚写进去的新值，
    // 「目标是不是停在刚才那个人身上」这个判断永远不成立，
    // 私聊结束后目标就一直卡在对方身上收不回来。
    const prev = prevPeerRef.current
    prevPeerRef.current = pmPeer
    setTarget((cur) => {
      if (pmPeer >= 0) return pmPeer
      // 私聊刚结束：只把停在「刚才那个人」上的目标收回来，
      // 用户自己另选的悄悄话对象不去动它。
      return cur === prev ? USER_ALL : cur
    })
  }, [pmPeer])
  const myName = me?.name ?? ''
  const names = useMemo(() => [...members.values()].map((m) => m.name).filter(Boolean), [members])

  // @候选：只在公开发言时提供，悄悄话里 @别人没有意义。
  const mq = target === USER_ALL && !mentionOff ? mentionQuery(draft, caret) : null
  const candidates = useMemo(() => {
    if (!mq) return []
    const pool = targets.map((m) => m.name).filter(Boolean)
    if (canBroadcast) pool.unshift(MENTION_ALL)
    return filterNames(pool, mq.query).slice(0, 8)
  }, [mq?.query, mq?.start, targets, canBroadcast])

  /** 写回文本并把光标放到指定位置。 */
  const [pendingCaret, setPendingCaret] = useState<number | null>(null)
  const putDraft = (next: { text: string; caret: number }) => {
    setDraft(next.text)
    setCaret(next.caret)
    setPendingCaret(next.caret)
  }
  // 必须在 DOM 提交后同步复位光标。之前用 requestAnimationFrame 延后一帧，
  // 用户紧接着敲的字会先落在末尾、再被拽回 @ 后面，整句被插乱。
  useLayoutEffect(() => {
    if (pendingCaret == null) return
    const ta = taRef.current
    if (ta) {
      ta.focus()
      ta.setSelectionRange(pendingCaret, pendingCaret)
    }
    setPendingCaret(null)
  }, [pendingCaret])
  const pickMention = (name: string) => {
    if (!mq) return
    putDraft(applyMention(draft, caret, mq.start, name))
    setMentionIdx(0)
  }
  const pickEmoji = (e: string) => {
    putDraft(insertAt(draft, caret, e))
    setEmojiOpen(false)
  }

  const submit = () => {
    const text = draft.trim()
    if (!text || !canWrite) return
    if (text.length > Limits.MaxTextLen) return
    sendText(text, target, target !== USER_ALL, broadcast)
    setDraft('')
  }

  return (
    <div className="panel chat-panel">
      <div className="panel-head">交流区</div>

      <div className="chat-list" ref={listRef} onScroll={onScroll}>
        {messages.map((m) => {
          const from = members.get(m.fromId)
          const name = m.name || from?.name || `席位 ${m.fromId}`
          // 席位号会在会话之间复用：历史消息里的 fromId 可能正好撞上现在的自己。
          // 消息自带发送者名字，名字对不上就不算「我的」。
          const mine = m.fromId === selfId && (!m.name || m.name === myName)
          return (
            <div
              key={m.key}
              className={`chat-msg${m.broadcast ? ' broadcast' : ''}${m.secret ? ' secret' : ''}${mine ? ' mine' : ''}${!mine && mentionsMe(m.text, myName, names) ? ' mentioned' : ''}`}
            >
              <div className="chat-meta">
                <span className="chat-name">{name}</span>
                {m.broadcast && <em className="tag tag-notice">公告</em>}
                {m.secret && (
                  <em className="tag tag-secret">
                    {mine ? `悄悄对 ${members.get(m.destId)?.name ?? m.destId} 说` : '悄悄话'}
                  </em>
                )}
                <span className="chat-time">{formatTime(m.time)}</span>
              </div>
              <div className="chat-text"><RichText text={m.text} names={names} myName={myName} /></div>
            </div>
          )
        })}
        {messages.length === 0 && <div className="chat-empty">还没有人发言</div>}
      </div>

      <div className="chat-compose">
        <div className="chat-opts">
          <select value={target} onChange={(e) => setTarget(Number(e.target.value))}>
            <option value={USER_ALL}>对所有人</option>
            {targets.map((m) => (
              <option key={m.id} value={m.id}>
                悄悄对 {m.name || `席位 ${m.id}`}
              </option>
            ))}
          </select>
          {canBroadcast && (
            <label className="chk">
              <input
                type="checkbox"
                checked={broadcast}
                onChange={(e) => setBroadcast(e.target.checked)}
              />
              作为公告
            </label>
          )}
        </div>
        <div className="chat-input-row">
          <div className="chat-ta-wrap">
            {candidates.length > 0 && (
              <MentionPop names={candidates} active={mentionIdx} onPick={pickMention} />
            )}
            <textarea
              ref={taRef}
              value={draft}
              disabled={!canWrite}
              placeholder={canWrite ? '说点什么…' : '你当前没有发言权限'}
              // 用法提示放 title，不塞进 placeholder：
              // 侧栏只有 300 出头的宽度，长 placeholder 会折成两行，
              // 把本来就矮的输入框顶得更挤。
              title={canWrite ? '输入 @ 可提醒某人，回车发送，Shift + 回车换行' : undefined}
              maxLength={Limits.MaxTextLen}
              onChange={(e) => { setDraft(e.target.value); setCaret(e.target.selectionStart ?? 0); setMentionIdx(0); setMentionOff(false) }}
              onSelect={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
              onKeyDown={(e) => {
                // 候选列表开着时，上下回车先归它。
                if (candidates.length > 0) {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx((i) => (i + 1) % candidates.length); return }
                  if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx((i) => (i - 1 + candidates.length) % candidates.length); return }
                  if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(candidates[mentionIdx] ?? candidates[0]); return }
                  if (e.key === 'Escape') { e.preventDefault(); setMentionOff(true); return }
                }
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  submit()
                }
              }}
            />
          </div>
          <div className="chat-emoji">
            <button
              type="button"
              className="chat-emoji-btn"
              title="表情"
              aria-label="表情"
              disabled={!canWrite}
              onClick={() => setEmojiOpen((v) => !v)}
            >
              <IcSmile size={19} />
            </button>
            {emojiOpen && <EmojiPicker onPick={pickEmoji} onClose={() => setEmojiOpen(false)} />}
          </div>
          <button className="primary" disabled={!canWrite || !draft.trim()} onClick={submit}>
            发送
          </button>
        </div>
      </div>
    </div>
  )
}

/** 把秒级时间戳格式化成 HH:MM。 */
function formatTime(sec: number): string {
  const d = new Date(sec * 1000)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}
